/**
 * BrowseContainersPage — public marketplace of open containers a sender
 * can join. Filterable by origin/destination country.
 *
 *   GET /api/cargo/containers/open
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight, Container, Loader, Hash, Globe, ArrowLeftRight,
  Calendar, ChevronLeft, Filter, X,
} from 'lucide-react';
import { useOpenContainers } from '@/hooks/useContainers';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/common/Button';
import {
  containerFillFraction, containerPriceLabel,
} from '@/utils/containerStatus';
import { CONTAINER_COUNTRIES } from '@/constants/countries';
import type { Container as ContainerT } from '@/api/containers';

const FIELD_CLASSES =
  'w-full rounded-xl border border-border bg-surface px-4 py-3 text-text-primary outline-none transition focus:border-primary';

export default function BrowseContainersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Server expects ISO2 lowercase. The select stores lowercase too,
  // so no transform needed when passing to the hook.
  const params = {
    originCountry: origin || undefined,
    destinationCountry: destination || undefined,
  };
  const { data: containers, isLoading, error } = useOpenContainers(params);

  const hasFilter = !!(origin || destination);

  return (
    <AppLayout>
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-text-secondary mb-4 hover:text-primary"
      >
        <ArrowRight size={18} /> {t('back', 'رجوع')}
      </button>

      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold text-primary mb-1 flex items-center gap-2">
              <Container size={20} /> {t('browse_containers_title', 'حاويات مفتوحة للانضمام')}
            </h1>
            <p className="text-text-secondary text-sm">
              {t('browse_containers_subtitle', 'استورد بضائعك بأقل التكاليف عبر الانضمام لحاوية مع مرسلين آخرين.')}
            </p>
          </div>
          <Button variant="ghost" onClick={() => setShowFilters((s) => !s)}>
            <Filter size={16} className="ml-1" />
            {hasFilter && (
              <span className="bg-primary text-white text-[10px] px-1.5 py-0.5 rounded-full mr-1">
                {[origin, destination].filter(Boolean).length}
              </span>
            )}
            {t('filter', 'فلترة')}
          </Button>
        </div>

        {showFilters && (
          <div className="bg-surface border border-border rounded-2xl p-4 mb-4">
            <div className="grid grid-cols-2 gap-3 mb-2">
              <Field label={t('origin_country', 'بلد المنشأ')}>
                <select
                  value={origin}
                  onChange={(e) => setOrigin(e.target.value)}
                  className={FIELD_CLASSES}
                >
                  <option value="">{t('all_countries', 'كل الدول')}</option>
                  {CONTAINER_COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
                </select>
              </Field>
              <Field label={t('destination_country', 'بلد الوجهة')}>
                <select
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  className={FIELD_CLASSES}
                >
                  <option value="">{t('all_countries', 'كل الدول')}</option>
                  {CONTAINER_COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
                </select>
              </Field>
            </div>
            {hasFilter && (
              <Button
                variant="ghost"
                onClick={() => { setOrigin(''); setDestination(''); }}
                type="button"
              >
                <X size={14} className="ml-1" /> {t('clear_filters', 'مسح الفلترة')}
              </Button>
            )}
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center py-12 text-text-tertiary">
            <Loader size={28} className="animate-spin" />
          </div>
        ) : error ? (
          <div className="bg-error/10 text-error rounded-xl p-4 text-sm text-center">
            {t('list_load_failed', 'تعذّر تحميل القائمة. حاول مجدداً.')}
          </div>
        ) : !containers || containers.length === 0 ? (
          <div className="bg-surface border border-border rounded-2xl p-10 text-center">
            <Container size={48} className="mx-auto text-text-tertiary mb-3" />
            <p className="text-text-secondary">
              {hasFilter
                ? t('no_containers_filter_match', 'لا حاويات تطابق الفلترة. جرّب مسارات أخرى.')
                : t('no_open_containers', 'لا حاويات مفتوحة حالياً. تابعنا لإشعارات لحظية بحاويات جديدة.')}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {containers.map((c) => <OpenContainerCard key={c.id} container={c} />)}
          </div>
        )}
      </div>
    </AppLayout>
  );
}

function OpenContainerCard({ container }: { container: ContainerT }) {
  const { t } = useTranslation();
  const fill = containerFillFraction(container);
  const priceLabel = containerPriceLabel(container);
  const remainingKg = container.remaining_weight_kg_approx;
  const remainingCbm = container.remaining_cbm_approx;

  return (
    <Link
      to={`/containers/${container.id}/join`}
      className="block bg-surface border border-border rounded-2xl p-4 hover:border-primary/40 transition-all"
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Hash size={14} className="text-text-tertiary" />
            <span className="font-bold text-text-primary" dir="ltr">{container.public_code}</span>
            {container.is_tentative && (
              <span className="text-[10px] px-1.5 py-0.5 bg-warning/10 text-warning rounded font-bold">
                {t('tentative', 'مبدئية')}
              </span>
            )}
          </div>
          <div className="text-sm text-text-primary mb-1 truncate">{container.title}</div>
          <div className="flex items-center gap-1 text-xs text-text-secondary">
            <Globe size={12} />
            <span dir="ltr">{container.origin_country?.toUpperCase()}</span>
            {container.origin_port_code && <span dir="ltr"> ({container.origin_port_code})</span>}
            <ArrowLeftRight size={12} />
            <span dir="ltr">{container.destination_country?.toUpperCase()}</span>
            {container.destination_port_code && <span dir="ltr"> ({container.destination_port_code})</span>}
          </div>
        </div>
        <ChevronLeft size={20} className="text-text-tertiary mt-1" />
      </div>

      {/* Capacity bar */}
      <div className="mt-3">
        <div className="flex justify-between text-xs text-text-secondary mb-1">
          <span>
            {t('available', 'متاح')}: <strong>
              {remainingKg != null ? `${Math.round(remainingKg).toLocaleString()} kg` : ''}
              {remainingCbm != null ? `${remainingKg != null ? ' · ' : ''}${remainingCbm.toFixed(2)} m³` : ''}
              {remainingKg == null && remainingCbm == null && '—'}
            </strong>
          </span>
          <span>{Math.round(fill * 100)}% {t('filled', 'ممتلئ')}</span>
        </div>
        <div className="w-full h-1.5 bg-border/40 rounded-full overflow-hidden">
          <div className="h-full bg-success transition-all" style={{ width: `${fill * 100}%` }} />
        </div>
      </div>

      <div className="flex flex-wrap gap-3 text-xs text-text-tertiary mt-3 pt-3 border-t border-border/50">
        {priceLabel && (
          <span><strong className="text-primary text-base">{priceLabel}</strong></span>
        )}
        {container.warehouse_cutoff_at && (
          <span className="flex items-center gap-1">
            <Calendar size={11} />
            {t('cutoff', 'إغلاق التسجيل')}: {new Date(container.warehouse_cutoff_at).toLocaleDateString('ar-SY')}
          </span>
        )}
        {container.estimated_arrival_at && (
          <span className="flex items-center gap-1">
            <Calendar size={11} />
            {t('eta', 'الوصول المتوقع')}: {new Date(container.estimated_arrival_at).toLocaleDateString('ar-SY')}
          </span>
        )}
      </div>
    </Link>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs text-text-secondary mb-1 block">{label}</span>
      {children}
    </label>
  );
}
